import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { api, type Project } from '@/lib/api';

/**
 * Loads a project + its assets. Auto-saves state changes (debounced 1s).
 */
export function useProject(projectId: string) {
  const qc = useQueryClient();

  const projectQ = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.projects.get(projectId),
    enabled: !!projectId && projectId !== 'demo',
  });

  const assetsQ = useQuery({
    queryKey: ['project-assets', projectId],
    queryFn: () => api.projects.listAssets(projectId),
    enabled: !!projectId && projectId !== 'demo',
  });

  const saveMutation = useMutation({
    mutationFn: (patch: { name?: string; description?: string; state?: unknown }) =>
      api.projects.update(projectId, patch),
    onSuccess: (updated) => {
      qc.setQueryData(['project', projectId], updated);
    },
  });

  return {
    project: projectQ.data ?? null,
    assets: assetsQ.data?.assets ?? [],
    isLoading: projectQ.isLoading || assetsQ.isLoading,
    error: projectQ.error || assetsQ.error,
    save: saveMutation.mutate,
    refetchAssets: assetsQ.refetch,
  };
}

/**
 * Debounced state-save hook.
 * Calls saveFn(state) at most once per debounceMs after state changes.
 */
export function useDebouncedSave<T>(state: T, saveFn: (s: T) => void, debounceMs = 1000) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedRef = useRef<T | null>(null);

  useEffect(() => {
    if (savedRef.current === null) {
      savedRef.current = state;
      return;
    }
    if (state === savedRef.current) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      saveFn(state);
      savedRef.current = state;
    }, debounceMs);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [state, saveFn, debounceMs]);
}

export type LoadedProject = Project | null;
